"use client";

import CreditCardIcon from "public/next-public/common/icons/credit-card-icon.svg";
import HouseIcon from "public/next-public/common/icons/house-icon.svg";
import IdLicenseIcon from "public/next-public/common/icons/id-license-icon.svg";
import PlusIcon from "public/next-public/common/icons/plus-solid.svg";

import Button from "@/components/design-system/button";
import Modal from "@/components/design-system/modal";

import style from "./autoOrderAccountSetupModal.module.scss";

/**
 * Shown when a subscribe attempt is blocked because the account is missing
 * billing, shipping, or license information. Rendered both from the product
 * detail / reorder flows and from the Auto Order dashboard's 422 NEEDS_INFO
 * path, so the two used to be separate components with divergent markup.
 *
 * Links open in a new tab on purpose — the user is mid-subscribe and we don't
 * want to navigate them out of the flow.
 */
function AutoOrderAccountSetupModal({
  show,
  onHide,
  missingBillingInfo,
  missingShippingInfo,
  missingLicenses,
}: AutoOrderAccountSetupModalProps) {
  return (
    <Modal size="skinny" sizeDownMd="medium" show={show} onHide={onHide} title="Complete Account Setup">
      <div className={style.content}>
        <p className={style.intro}>
          In order to use Auto Order, you must first complete your account setup. Please follow the link(s) below to
          provide your information.
        </p>

        {missingBillingInfo && (
          <div className={style.setupRow}>
            <div className={style.iconAndLabel}>
              <span className={style.rowIcon}>
                <CreditCardIcon width={32} height={32} aria-hidden />
              </span>
              <span className={style.rowLabel}>Billing Information</span>
            </div>
            <Button
              variant="tertiary"
              color="blue"
              size="large"
              text="Add Payment Method"
              leftIcon={<PlusIcon width={16} height={16} aria-hidden />}
              showRightIcon={false}
              link={{ href: "/account/payment/new", target: "_blank" }}
              className={style.addButton}
            />
          </div>
        )}

        {missingShippingInfo && (
          <div className={style.setupRow}>
            <div className={style.iconAndLabel}>
              <span className={style.rowIcon}>
                <HouseIcon width={32} height={32} aria-hidden />
              </span>
              <span className={style.rowLabel}>Delivery Address</span>
            </div>
            <Button
              variant="tertiary"
              color="blue"
              size="large"
              text="Add Delivery Address"
              leftIcon={<PlusIcon width={16} height={16} aria-hidden />}
              showRightIcon={false}
              link={{ href: "/account/shipping/new", target: "_blank" }}
              className={style.addButton}
            />
          </div>
        )}

        {missingLicenses && (
          <div className={style.licenseGroup}>
            <div className={style.setupRow}>
              <div className={style.iconAndLabel}>
                <span className={style.rowIcon}>
                  <IdLicenseIcon width={32} height={32} aria-hidden />
                </span>
                <span className={style.rowLabel}>Dental License</span>
              </div>
              <Button
                variant="tertiary"
                color="blue"
                size="large"
                text="Add License Information"
                leftIcon={<PlusIcon width={16} height={16} aria-hidden />}
                showRightIcon={false}
                link={{ href: "/account/license", target: "_blank" }}
                className={style.addButton}
              />
            </div>
            <p className={style.disclaimer}>The item you are trying to add to Auto Order requires a dental license</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

export interface AutoOrderAccountSetupModalProps {
  show: boolean;
  onHide: () => void;
  missingBillingInfo: boolean;
  missingShippingInfo: boolean;
  missingLicenses: boolean;
}

export default AutoOrderAccountSetupModal;
